import { ConfigProvider } from "effect/ConfigProvider";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import { FileSystem } from "effect/FileSystem";
import * as Layer from "effect/Layer";
import * as Logger from "effect/Logger";
import * as Option from "effect/Option";
import { Path } from "effect/Path";
import * as Scope from "effect/Scope";
import * as FetchHttpClient from "effect/unstable/http/FetchHttpClient";
import { AlchemyContext, AlchemyContextLive } from "./AlchemyContext.js";
import { provideFreshArtifactStore } from "./Artifacts.js";
import { AuthProviders } from "./Auth/AuthProvider.js";
import { CredentialsStore, CredentialsStoreLive } from "./Auth/Credentials.js";
import { AlchemyProfile, ProfileLive } from "./Auth/Profile.js";
import { Cli } from "./Cli/Cli.js";
import * as Output from "./Output.js";
import { Stage } from "./Stage.js";
import { loadConfigProvider } from "./Util/ConfigProvider.js";
import { effectClass, taggedFunction } from "./Util/effect.js";
import { fileLogger } from "./Util/FileLogger.js";
import { PlatformServices } from "./Util/PlatformServices.js";
export const Stack = Object.assign(taggedFunction(Context.Service()("Stack"), (stackName, options, eff) => {
    if (!stackName) {
        return (stackName) => Object.assign(
        // by default, reference the stack at the "current" stage of the importer
        Output.stackRef(stackName).pipe(effectClass), {
            stackName,
            stage: createStageProxy(stackName),
            state: options?.state,
            providers: options?.providers,
            make: (options, eff) => 
            // @ts-expect-error
            Stack(stackName, options, eff),
        });
    }
    return eff.pipe(
    // @ts-expect-error
    make({
        name: stackName,
        ...options,
    }), (eff) => Object.assign(eff, {
        stackName,
        stage: createStageProxy(stackName),
        state: options?.state,
        providers: options?.providers,
    }));
}));
const createStageProxy = (stackName) => new Proxy({}, {
    get: (_, stage) => Output.stackRef(stackName, { stage }),
});
export const StackName = Stack.use((stack) => Effect.succeed(stack.name));
export const make = (options) => (effect) => Effect.scope.pipe(Effect.flatMap((scope) => {
    if (options.state == null) {
        return Effect.die(new Error(`Stack "${options.name}" is missing a state store. ` +
            `Add a \`state\` layer to the stack options, e.g.:\n` +
            `  Alchemy.Stack("${options.name}", {\n` +
            `    providers: Cloudflare.providers(),\n` +
            `    state: Cloudflare.state(), // <-- required\n` +
            `  }, ...)\n` +
            `See https://alchemy.run/state-store for available state stores.`));
    }
    if (options.providers == null) {
        return Effect.die(new Error(`Stack "${options.name}" is missing a providers layer. ` +
            `Add a \`providers\` layer to the stack options, e.g.:\n` +
            `  Alchemy.Stack("${options.name}", {\n` +
            `    providers: Cloudflare.providers(), // <-- required\n` +
            `    state: Cloudflare.state(),\n` +
            `  }, ...)`));
    }
    return options.providers.pipe(Layer.provideMerge(options.state), Layer.provideMerge(Layer.effect(Stack, Stage.pipe(Effect.map((stage) => options.stack ?? {
        name: options.name,
        stage,
        resources: {},
        bindings: {},
        actions: {},
    })))), Layer.buildWithScope(scope));
}), Effect.flatMap((context) => Effect.all([
    effect,
    Stack,
    Effect.context(),
]).pipe(Effect.map(([output, stack, services]) => ({
    ...stack,
    output,
    services: Context.merge(services, context),
})), Effect.provideContext(context))));
export const CurrentStack = Effect.serviceOption(Stack)
    .pipe(Effect.map(Option.getOrUndefined));
const platform = Layer.mergeAll(PlatformServices, FetchHttpClient.layer, Logger.layer([fileLogger("out")], { mergeWithExisting: true }), Layer.provide(ProfileLive, PlatformServices), Layer.provide(CredentialsStoreLive, PlatformServices));
// override alchemy state store, CLI/reporting, state, and Config
const alchemy = (overrides) => Layer.mergeAll(
// CLI.inkCLI(),
// optional
overrides?.dev
    ? Layer.provide(Layer.effect(AlchemyContext, AlchemyContext.useSync((ctx) => ({ ...ctx, dev: overrides.dev }))), AlchemyContextLive)
    : AlchemyContextLive);
export const evalStack = (effect, fn, options) => {
    const body = Effect.gen(function* () {
        const stack = yield* effect;
        const configProvider = yield* loadConfigProvider(Option.none());
        return yield* fn(stack).pipe(provideFreshArtifactStore, Effect.provide(Layer.succeedContext(stack.services).pipe(Layer.provideMerge(Layer.succeed(ConfigProvider, configProvider)))));
    }).pipe(Effect.provide(Layer.effect(AuthProviders, Effect.serviceOption(AuthProviders).pipe(Effect.map(Option.getOrElse(() => ({}))))).pipe(Layer.provideMerge(Layer.succeed(Stage, options.stage)), Layer.provideMerge(Layer.provideMerge(alchemy({ dev: options.dev }), platform)))));
    return options.scope === undefined
        ? Effect.scoped(body)
        : Scope.provide(body, options.scope);
};
//# sourceMappingURL=Stack.js.map